import  { useState, useEffect, useRef } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, Users, Clock, AlertCircle, Bell} from 'lucide-react';

interface CallData {
  id: string;
  roomId: string;
  participants: number;
  duration: string;
  status: 'active' | 'waiting' | 'ended';
  callerName?: string;
}

interface Notification {
  id: string;
  message: string;
  type: 'incoming' | 'joined' | 'left';
  timestamp: Date;
}

interface CallAlert {
  id: string;
  roomId: string;
  callerName: string;
  phoneNumber?: string;
  timestamp: Date;
  lineNumber: string;
}

const CallCenter = () => {
  const [calls, setCalls] = useState<CallData[]>([
    {
      id: '1',
      roomId: 'call-room-1',
      participants: 0,
      duration: '00:00',
      status: 'waiting',
      callerName: 'Esperando llamada...'
    },
    {
      id: '2',
      roomId: 'call-room-2',
      participants: 0,
      duration: '00:00',
      status: 'waiting',
      callerName: 'Esperando llamada...'
    },
    {
      id: '3',
      roomId: 'call-room-3',
      participants: 0,
      duration: '00:00',
      status: 'waiting',
      callerName: 'Esperando llamada...'
    }
  ]);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [callAlerts, setCallAlerts] = useState<CallAlert[]>([]);
  const [playRingtone, setPlayRingtone] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState<{[key: string]: boolean}>({
    '1': true,
    '2': true,
    '3': true
  });
  const [videoEnabled, setVideoEnabled] = useState<{[key: string]: boolean}>({
    '1': true,
    '2': true,
    '3': true
  });

  const jitsiRefs = useRef<{[key: string]: any}>({});
  const audioRef = useRef<HTMLAudioElement>(null);

  // Efecto para el sonido de llamada
  useEffect(() => {
    if (playRingtone && audioRef.current) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [playRingtone]);

  // Simulación de FCM listener - aquí conectarías con Firebase
  useEffect(() => {
    const initializeFCM = () => {
      // Aquí iría la inicialización real de FCM

      // Simular llamadas entrantes
    //   const interval = setInterval(simulateIncomingCall, Math.random() * 20000 + 15000);
    //   return () => clearInterval(interval);
    };

    return initializeFCM();
  }, []);

  // Inicializar Jitsi Meet iframes
  useEffect(() => {
    calls.forEach(call => {
      if (call.status === 'active' && !jitsiRefs.current[call.id]) {
        const iframe = document.getElementById(`jitsi-${call.id}`) as HTMLIFrameElement;
        if (iframe) {
          iframe.src = `https://video.pas-hq.dev/${call.roomId}#config.startWithAudioMuted=true&config.startWithVideoMuted=true`;
        }
      }
    });
  }, [calls]);

  const acceptCall = (alertId: string) => {
    const alert = callAlerts.find(a => a.id === alertId);
    if (alert) {
      // Actualizar estado de la llamada
      setCalls(prev => prev.map(call => 
        call.id === alert.lineNumber 
          ? { ...call, status: 'active' as const, participants: 1, callerName: alert.callerName }
          : call
      ));
      
      // Remover alerta
      setCallAlerts(prev => prev.filter(a => a.id !== alertId));
      setPlayRingtone(false);
      
      // Agregar notificación
      const notification: Notification = {
        id: Date.now().toString() + '_accept',
        message: `Llamada aceptada de ${alert.callerName} en Línea ${alert.lineNumber}`,
        type: 'joined',
        timestamp: new Date()
      };
      setNotifications(prev => [notification, ...prev.slice(0, 4)]);
    }
  };

  const rejectCall = (alertId: string) => {
    const alert = callAlerts.find(a => a.id === alertId);
    if (alert) {
      setCallAlerts(prev => prev.filter(a => a.id !== alertId));
      setPlayRingtone(false);
      
      // Agregar notificación
      const notification: Notification = {
        id: Date.now().toString() + '_reject',
        message: `Llamada rechazada de ${alert.callerName}`,
        type: 'left',
        timestamp: new Date()
      };
      setNotifications(prev => [notification, ...prev.slice(0, 4)]);
    }
  };

  const toggleAudio = (callId: string) => {
    setAudioEnabled(prev => ({ ...prev, [callId]: !prev[callId] }));
    // Aquí integrarías con la API de Jitsi para controlar el audio
  };

  const toggleVideo = (callId: string) => {
    setVideoEnabled(prev => ({ ...prev, [callId]: !prev[callId] }));
    // Aquí integrarías con la API de Jitsi para controlar el video
  };

  const endCall = (callId: string) => {
    setCalls(prev => prev.map(call => 
      call.id === callId 
        ? { ...call, status: 'waiting' as const, participants: 0, callerName: 'Esperando llamada...', duration: '00:00' }
        : call
    ));
    
    // Agregar notificación
    const notification: Notification = {
      id: Date.now().toString() + '_end',
      message: `Llamada terminada en Línea ${callId}`,
      type: 'left',
      timestamp: new Date()
    };
    setNotifications(prev => [notification, ...prev.slice(0, 4)]);
    // Aquí integrarías con la API de Jitsi para terminar la llamada
  };

  const getStatusColor = (status: CallData['status']) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'waiting': return 'bg-gray-400';
      case 'ended': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'incoming': return <Phone className="h-4 w-4 text-green-500" />;
      case 'joined': return <Users className="h-4 w-4 text-blue-500" />;
      case 'left': return <PhoneOff className="h-4 w-4 text-red-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Audio para ringtone */}
      <audio 
        ref={audioRef} 
        loop 
        preload="auto"
        onEnded={() => setPlayRingtone(false)}
      >
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAaBO2Q2e/NdCUFl14+" type="audio/wav" />
      </audio>

      {/* Call Alerts - Ventanas emergentes */}
      {callAlerts.map(alert => (
        <div key={alert.id} className="fixed top-4 right-4 z-50 animate-bounce">
          <div className="bg-white border-l-4 border-green-500 rounded-lg shadow-2xl p-6 min-w-80 max-w-md">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Phone className="h-8 w-8 text-green-500 animate-pulse" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">Llamada Entrante</h3>
                  <p className="text-sm text-gray-600">Línea {alert.lineNumber}</p>
                </div>
              </div>
              <Bell className="h-6 w-6 text-yellow-500 animate-bounce" />
            </div>
            
            <div className="mb-6">
              <p className="font-semibold text-gray-900 text-lg">{alert.callerName}</p>
              {alert.phoneNumber && (
                <p className="text-gray-600 text-sm">{alert.phoneNumber}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {alert.timestamp.toLocaleTimeString()}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => acceptCall(alert.id)}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 transform hover:scale-105"
              >
                <Phone className="h-5 w-5" />
                Aceptar
              </button>
              <button
                onClick={() => rejectCall(alert.id)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 transform hover:scale-105"
              >
                <PhoneOff className="h-5 w-5" />
                Rechazar
              </button>
            </div>
          </div>
        </div>
      ))}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Centro de Llamadas</h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-600">Operador en línea</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">
                {calls.filter(call => call.status === 'active').length} llamadas activas
              </span>
            </div>
          </div>
        </div>

        {/* Notifications Panel */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-blue-500" />
            Notificaciones
          </h2>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="text-gray-500 text-sm">No hay notificaciones</p>
            ) : (
              notifications.map(notification => (
                <div key={notification.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  {getNotificationIcon(notification.type)}
                  <span className="text-sm text-gray-700 flex-1">{notification.message}</span>
                  <span className="text-xs text-gray-400">
                    {notification.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Calls Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {calls.map((call) => (
            <div key={call.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Call Header */}
              <div className="p-4 bg-gray-50 border-b">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">Línea {call.id}</h3>
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(call.status)}`}></div>
                </div>
                <div className="text-sm text-gray-600 mb-1">{call.callerName}</div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {call.participants}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {call.duration}
                  </span>
                </div>
              </div>

              {/* Video Container */}
              <div className="relative bg-gray-900 aspect-video">
                {call.status === 'active' ? (
                  <iframe
                    id={`jitsi-${call.id}`}
                    className="w-full h-full"
                    allow="camera; microphone; fullscreen; display-capture"
                    title={`Jitsi Meet Room ${call.roomId}`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <Phone className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Esperando llamada...</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="p-4 bg-gray-50">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => toggleAudio(call.id)}
                    className={`p-2 rounded-full transition-colors ${
                      audioEnabled[call.id] 
                        ? 'bg-green-500 hover:bg-green-600 text-white' 
                        : 'bg-red-500 hover:bg-red-600 text-white'
                    }`}
                    disabled={call.status !== 'active'}
                  >
                    {audioEnabled[call.id] ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                  </button>
                  
                  <button
                    onClick={() => toggleVideo(call.id)}
                    className={`p-2 rounded-full transition-colors ${
                      videoEnabled[call.id] 
                        ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                        : 'bg-gray-500 hover:bg-gray-600 text-white'
                    }`}
                    disabled={call.status !== 'active'}
                  >
                    {videoEnabled[call.id] ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                  </button>
                  
                  <button
                    onClick={() => endCall(call.id)}
                    className="p-2 rounded-full bg-red-500 hover:bg-red-600 text-white transition-colors"
                    disabled={call.status !== 'active'}
                  >
                    <PhoneOff className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Stats */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-500">
                {calls.filter(call => call.status === 'active').length}
              </div>
              <div className="text-sm text-gray-600">Activas</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-500">
                {calls.filter(call => call.status === 'waiting').length}
              </div>
              <div className="text-sm text-gray-600">En espera</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-500">
                {calls.reduce((sum, call) => sum + call.participants, 0)}
              </div>
              <div className="text-sm text-gray-600">Participantes</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-500">
                {notifications.length}
              </div>
              <div className="text-sm text-gray-600">Historial</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallCenter;